import React, {useEffect, useState, useRef} from "react";
import * as Sentry from '@sentry/browser';
import Api from "../../Api";
import classes from "./styles.module.css";

/**
 * MerchantTerminalSelect — каскадный выбор мерчант → терминал
 *
 * Режим user:
 *   Загружает getMerchantBalances(), группирует по merchant_name
 *   Возвращает merchant_settings_id
 *
 * Режим admin:
 *   Загружает getProjects() → getProjectServices(uuid)
 *   Возвращает terminal_id
 *
 * Props:
 *   mode: 'user' | 'admin'
 *   token: JWT token
 *   onSelect: function(value) — вызывается при выборе терминала
 *   label: строка (опционально)
 *   onDataLoaded: function(data) — вызывается когда данные загружены (опционально)
 */
function MerchantTerminalSelect(props) {
    var mode = props.mode || 'user';
    var token = props.token;
    var onSelect = props.onSelect;
    var onMerchantSelect = props.onMerchantSelect;
    var onDataLoaded = props.onDataLoaded;
    var stackOnMobile = props.stackOnMobile === true;

    // --- User mode state ---
    var _balances = useState([]);
    var balances = _balances[0];
    var setBalances = _balances[1];

    // --- Admin mode state ---
    var _merchants = useState([]);
    var merchants = _merchants[0];
    var setMerchants = _merchants[1];

    var _terminals = useState([]);
    var terminals = _terminals[0];
    var setTerminals = _terminals[1];

    var _terminalsLoading = useState(false);
    var terminalsLoading = _terminalsLoading[0];
    var setTerminalsLoading = _terminalsLoading[1];

    // --- Common state ---
    var _selectedMerchant = useState("");
    var selectedMerchant = _selectedMerchant[0];
    var setSelectedMerchant = _selectedMerchant[1];

    var _selectedTerminal = useState("");
    var selectedTerminal = _selectedTerminal[0];
    var setSelectedTerminal = _selectedTerminal[1];

    var _merchantSearch = useState("");
    var merchantSearch = _merchantSearch[0];
    var setMerchantSearch = _merchantSearch[1];

    var _terminalSearch = useState("");
    var terminalSearch = _terminalSearch[0];
    var setTerminalSearch = _terminalSearch[1];

    // The selected label is display text, not a search entered by the user.
    var _terminalQuery = useState("");
    var terminalQuery = _terminalQuery[0];
    var setTerminalQuery = _terminalQuery[1];

    var _showMerchantDropdown = useState(false);
    var showMerchantDropdown = _showMerchantDropdown[0];
    var setShowMerchantDropdown = _showMerchantDropdown[1];

    var _showTerminalDropdown = useState(false);
    var showTerminalDropdown = _showTerminalDropdown[0];
    var setShowTerminalDropdown = _showTerminalDropdown[1];

    var _loading = useState(true);
    var loading = _loading[0];
    var setLoading = _loading[1];

    var dropdownRef = useRef(null);
    var terminalDropdownRef = useRef(null);
    var inputRef = useRef(null);

    // Close dropdown on outside click
    useEffect(function() {
        function handleClickOutside(e) {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowMerchantDropdown(false);
            }
            if (terminalDropdownRef.current && !terminalDropdownRef.current.contains(e.target)) {
                setShowTerminalDropdown(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return function() {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Load data
    useEffect(function() {
        var api = new Api({token: token});

        if (mode === 'user') {
            api.getMerchantBalances().then(function(r) {
                var list = (r && r.balances) ? r.balances : [];
                setBalances(list);

                // Build unique merchants list
                var merchantMap = {};
                list.forEach(function(b) {
                    var key = b.merchant_uuid || b.merchant_name || 'unknown';
                    if (!merchantMap[key]) {
                        merchantMap[key] = {
                            uuid: b.merchant_uuid || '',
                            name: b.merchant_name || 'Мерчант'
                        };
                    }
                });
                var uniqueMerchants = Object.keys(merchantMap).map(function(k) {
                    return merchantMap[k];
                });
                // Добавляем "Все проекты" если больше одного мерчанта
                if (uniqueMerchants.length > 1) {
                    uniqueMerchants.unshift({uuid: '__all__', name: 'Все проекты'});
                }
                setMerchants(uniqueMerchants);

                // Auto-select first real merchant (skip __all__)
                if (uniqueMerchants.length > 0) {
                    var firstReal = uniqueMerchants.find(function(m) { return m.uuid !== '__all__'; });
                    if (firstReal) {
                        setSelectedMerchant(firstReal.uuid);
                        setMerchantSearch(firstReal.name);

                        var firstTerminals = list.filter(function(b) {
                            return (b.merchant_uuid || '') === firstReal.uuid;
                        });
                        if (firstTerminals.length > 0) {
                            var firstMsId = String(firstTerminals[0].merchant_settings_id);
                            setSelectedTerminal(firstMsId);
                            setTerminalSearch(getTerminalLabel(firstTerminals[0]));
                            if (onSelect) onSelect(firstMsId);
                        }
                    }
                }

                if (onDataLoaded) onDataLoaded(list);
                setLoading(false);
            }).catch(function(err) {
                Sentry.captureException(err);
                setLoading(false);
            });
        } else {
            // Admin mode — загружаем список всех мерчантов
            api.getProjectsList({itemsPerPage: 1000}).then(function(r) {
                var list = (r && r.data) ? r.data : (Array.isArray(r) ? r : []);
                setMerchants(list);
                setLoading(false);
            }).catch(function(err) {
                Sentry.captureException(err);
                setLoading(false);
            });
        }
    }, [token, mode]);

    // Get terminal options for user mode
    function getUserTerminals() {
        if (!selectedMerchant) return [];
        if (selectedMerchant === '__all__') return balances;
        return balances.filter(function(b) {
            return (b.merchant_uuid || '') === selectedMerchant;
        });
    }

    // Handle merchant selection (admin mode) - load terminals
    function selectMerchantAdmin(merchant) {
        setSelectedMerchant(merchant.uuid);
        setMerchantSearch(merchant.name);
        setShowMerchantDropdown(false);
        setShowTerminalDropdown(false);
        setSelectedTerminal("");
        setTerminalSearch("");
        setTerminals([]);

        if (onSelect) onSelect("");
        if (onMerchantSelect) onMerchantSelect(merchant.uuid);

        setTerminalsLoading(true);
        var api = new Api({token: token});
        api.getProjectServices(merchant.uuid).then(function(r) {
            var list = Array.isArray(r) ? r : (r && r.data ? r.data : []);
            setTerminals(list);
            setTerminalsLoading(false);

            // Auto-select first terminal
            if (list.length > 0) {
                var firstId = String(list[0].id);
                setSelectedTerminal(firstId);
                setTerminalSearch(getTerminalLabel(list[0]));
                if (onSelect) onSelect(firstId);
            }
        }).catch(function(err) {
            Sentry.captureException(err);
            setTerminalsLoading(false);
        });
    }

    // Handle merchant selection (user mode)
    function selectMerchantUser(merchant) {
        setSelectedMerchant(merchant.uuid);
        setMerchantSearch(merchant.name);
        setShowMerchantDropdown(false);
        setShowTerminalDropdown(false);

        if (merchant.uuid === '__all__') {
            // "Все проекты" → выбираем "Все терминалы"
            var allMsIds = balances.map(function(b) { return b.merchant_settings_id; }).join(',');
            setSelectedTerminal('__all__');
            setTerminalSearch('Все терминалы');
            if (onSelect) onSelect(allMsIds);
            return;
        }

        // Get terminals for this merchant
        var termList = balances.filter(function(b) {
            return (b.merchant_uuid || '') === merchant.uuid;
        });

        if (termList.length > 0) {
            // Если больше одного терминала, по умолчанию "Все терминалы"
            if (termList.length > 1) {
                var allIds = termList.map(function(b) { return b.merchant_settings_id; }).join(',');
                setSelectedTerminal('__all__');
                setTerminalSearch('Все терминалы');
                if (onSelect) onSelect(allIds);
            } else {
                var firstMsId = String(termList[0].merchant_settings_id);
                setSelectedTerminal(firstMsId);
                setTerminalSearch(getTerminalLabel(termList[0]));
                if (onSelect) onSelect(firstMsId);
            }
        } else {
            setSelectedTerminal("");
            setTerminalSearch("");
            if (onSelect) onSelect("");
        }
    }

    // Handle terminal change (user mode)
    function handleTerminalChangeUser(e) {
        var val = e.target.value;
        setSelectedTerminal(val);
        if (val === '__all__') {
            var termList = getUserTerminals();
            var allIds = termList.map(function(b) { return b.merchant_settings_id; }).join(',');
            setTerminalSearch('Все терминалы');
            if (onSelect) onSelect(allIds);
        } else {
            var selected = getUserTerminals().find(function(b) {
                return String(b.merchant_settings_id) === String(val);
            });
            setTerminalSearch(selected ? getTerminalLabel(selected) : '');
            if (onSelect) onSelect(val);
        }
    }

    // Handle terminal change (admin mode)
    function handleTerminalChangeAdmin(e) {
        var val = e.target.value;
        setSelectedTerminal(val);
        var selected = terminals.find(function(t) {
            return String(t.id) === String(val);
        });
        setTerminalSearch(selected ? getTerminalLabel(selected) : '');
        if (onSelect) onSelect(val);
    }

    function getTerminalOptionsList() {
        var list = mode === 'user' ? getUserTerminals() : terminals;

        if (mode === 'user' && list.length > 1) {
            return [{merchant_settings_id: '__all__', terminal_name: 'Все терминалы', terminal_id: ''}].concat(list);
        }

        return list;
    }

    function getTerminalLabel(item) {
        var name = item && item.terminal_name ? item.terminal_name : (item && item.name ? item.name : 'Терминал');
        var id = item && (item.merchant_settings_id || item.id);

        if (item && item.merchant_settings_id === '__all__') {
            return 'Все терминалы';
        }

        return id ? name + ' (ID: ' + id + ')' : name;
    }

    // Filtered merchants for search
    function getFilteredMerchants() {
        if (!merchantSearch) return merchants;
        var q = merchantSearch.toLowerCase();
        return merchants.filter(function(m) {
            var name = (m.name || '').toLowerCase();
            var uuid = (m.uuid || '').toLowerCase();
            return name.indexOf(q) !== -1 || uuid.indexOf(q) !== -1;
        });
    }

    if (loading) {
        return (
            <div className={classes.SelectorWrapper + (stackOnMobile ? ' ' + classes.StackOnMobile : '')}>
                <div className={classes.SelectorGroup}>
                    <label>{props.label || "Терминал"}</label>
                    <div style={{color: "#6c757d", fontSize: "0.875rem"}}>Загрузка...</div>
                </div>
            </div>
        );
    }

    var filteredMerchants = getFilteredMerchants();

    var terminalOptionsList = getTerminalOptionsList();
    var filteredTerminals = terminalOptionsList.filter(function(t) {
        if (!terminalQuery) return true;
        var q = terminalQuery.toLowerCase();
        var label = getTerminalLabel(t).toLowerCase();
        var id = String(t.merchant_settings_id || t.id || '').toLowerCase();
        return label.indexOf(q) !== -1 || id.indexOf(q) !== -1;
    });

    return (
        <div className={classes.SelectorWrapper + (stackOnMobile ? ' ' + classes.StackOnMobile : '')}>
            <div className={classes.SelectorGroup} ref={dropdownRef}>
                <label>Мерчант</label>
                <div className={classes.SearchableSelect}>
                    <input
                        ref={inputRef}
                        type="text"
                        className={classes.SearchInput}
                        placeholder="Поиск мерчанта..."
                        value={merchantSearch}
                        onChange={function(e) {
                            setMerchantSearch(e.target.value);
                            setShowMerchantDropdown(true);
                        }}
                        onFocus={function() {
                            setShowMerchantDropdown(true);
                        }}
                    />
                    {showMerchantDropdown && (
                        <div className={classes.Dropdown}>
                            {filteredMerchants.length === 0 ? (
                                <div className={classes.DropdownEmpty}>Не найдено</div>
                            ) : (
                                filteredMerchants.map(function(m) {
                                    var isActive = selectedMerchant === m.uuid;
                                    var itemClass = classes.DropdownItem + (isActive ? ' ' + classes.DropdownItemActive : '');
                                    return (
                                        <div
                                            key={m.uuid}
                                            className={itemClass}
                                            onClick={function() {
                                                if (mode === 'user') {
                                                    selectMerchantUser(m);
                                                } else {
                                                    selectMerchantAdmin(m);
                                                }
                                            }}
                                        >
                                            {m.name}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className={classes.SelectorGroup} ref={terminalDropdownRef}>
                <label>{props.label || "Терминал"}</label>
                {terminalsLoading ? (
                    <div style={{color: "#6c757d", fontSize: "0.875rem", padding: "6px 0"}}>Загрузка терминалов...</div>
                ) : (
                    <div className={classes.SearchableSelect}>
                        <input
                            type="text"
                            className={classes.SearchInput}
                            placeholder="Поиск терминала..."
                            value={terminalSearch}
                            onChange={function(e) {
                                setTerminalSearch(e.target.value);
                                setTerminalQuery(e.target.value);
                                setShowTerminalDropdown(true);
                            }}
                            onFocus={function() {
                                setTerminalQuery("");
                                setShowTerminalDropdown(true);
                            }}
                        />
                        {showTerminalDropdown && (
                            <div className={classes.Dropdown}>
                                {filteredTerminals.length === 0 ? (
                                    <div className={classes.DropdownEmpty}>Не найдено</div>
                                ) : (
                                    filteredTerminals.map(function(t) {
                                        var value = String(t.merchant_settings_id || t.id || '');
                                        var isActive = selectedTerminal === value;
                                        var itemClass = classes.DropdownItem + (isActive ? ' ' + classes.DropdownItemActive : '');
                                        return (
                                            <div
                                                key={value}
                                                className={itemClass}
                                                onClick={function() {
                                                    setShowTerminalDropdown(false);
                                                    if (mode === 'user' && t.merchant_settings_id === '__all__') {
                                                        var termList = getUserTerminals();
                                                        var allIds = termList.map(function(b) { return b.merchant_settings_id; }).join(',');
                                                        setSelectedTerminal('__all__');
                                                        setTerminalSearch('Все терминалы');
                                                        if (onSelect) onSelect(allIds);
                                                        return;
                                                    }

                                                    setSelectedTerminal(value);
                                                    setTerminalSearch(getTerminalLabel(t));
                                                    if (onSelect) onSelect(value);
                                                }}
                                            >
                                                {getTerminalLabel(t)}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default MerchantTerminalSelect;



// WEBPACK FOOTER //
// ./src/Components/MerchantTerminalSelect/index.js